'use client';

import { useMemo, type ReactNode } from 'react';
import { DeliveryType } from '@/features/enums/DeliveryType';
import { Address } from '@/shared/model/Address';
import type { PageComponentData } from '@/widgets/prefab-renderer';
import { useGetVenueHrefAttributes } from '../../hooks/useGetVenueHrefAttributes';
import { VenueProvider } from '../../providers/VenueProvider';
import { CompleteVenueWithGeoData } from '../../types/CompleteVenueWithGeoData';
import './styles.scss';

type RenderVenueCards = (props: { data: PageComponentData[] }) => ReactNode;

let renderVenueCards: RenderVenueCards | null = null;

/** Connected once the prefab module has finished initializing. */
export function registerVenueCardRenderer(render: RenderVenueCards) {
    renderVenueCards = render;
}

function getVenueCardRenderer(): RenderVenueCards {
    if (renderVenueCards === null) {
        throw new Error('Venue card renderer is not registered');
    }
    return renderVenueCards;
}

type Props = {
    heading: string;
    venues: CompleteVenueWithGeoData[];
    venueCard?: PageComponentData;
    deliveryType: DeliveryType;
    address?: Address | null;
};

export const SelectDeliveryBoxVenueList = ({
    heading,
    venues,
    venueCard,
    deliveryType,
    address = null,
}: Props) => {
    const cardRef: PageComponentData[] = useMemo(
        () => (venueCard ? [venueCard] : [{ prefab: 'VenueCard' }]),
        [venueCard],
    );
    const getVenueHrefAttributes = useGetVenueHrefAttributes();
    const VenueCards = getVenueCardRenderer();
    return (
        <div className="select-delivery-box__list" data-testid="select-delivery-box-venue-list">
            <h3 className="select-delivery-box__list-heading">{heading}</h3>
            <div className="select-delivery-box__list-items">
                {venues.map((venue) => (
                    // The card renders its own <a>; feed it the router href (chosen delivery type + address).
                    <div key={venue.apiPartnerSettingsId} className="select-delivery-box__list-item">
                        <VenueProvider
                            value={{
                                data: venue,
                                ...getVenueHrefAttributes(venue.apiPartnerSettingsId, {
                                    address,
                                    deliveryType,
                                }),
                                deliveryType,
                                address,
                            }}
                        >
                            <VenueCards data={cardRef} />
                        </VenueProvider>
                    </div>
                ))}
            </div>
        </div>
    );
};
